import { z } from "zod";

import { AgentKnowledgeSchema } from "../../../../schemas/Agent/knowledges.schema";
import { ModelConfigSchema } from "../../../../schemas/ModelConfig/model-config.schema";
import { AgentStatusSchema } from "./agent.schema";

/**
 * AgentToolConfig sub-schema
 *
 * Fields from AgentToolConfig relation included in the agent detail response.
 * Field set covers all usages in ProfileView and tiptap editor.
 */
export const AgentToolConfigDetailSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  iconUrl: z.string().nullable(),
  iconType: z.string(),
  type: z.string(),
  isActive: z.boolean(),
  agentInputSchema: z.unknown()
});

export type AgentToolConfigDetail = z.infer<typeof AgentToolConfigDetailSchema>;

/**
 * Deployment-derived tool sub-schema
 *
 * Tools derived from linked deployment settings (e.g. sendSms[] from TWILIO_VOICE).
 * Not stored in AgentToolConfig — read from Deployment.deploymentSettings at query time.
 */
export const DeploymentToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  toolType: z.string(),
  deploymentId: z.string(),
  deploymentName: z.string(),
  deploymentType: z.string()
});

export type DeploymentTool = z.infer<typeof DeploymentToolSchema>;

/**
 * AgentDetailSchema
 *
 * Response schema for GET /api/agents/:id.
 *
 * Naming follows the existing Prisma/API convention:
 * - snake_case for @map fields: first_name, last_name, profile_picture, created_at, updated_at
 * - camelCase for standard Prisma fields: modelName, organizationId, promptJson, etc.
 */
export const AgentDetailSchema = z.object({
  // Identity
  id: z.string(),
  organizationId: z.string().nullable(),

  // Profile (snake_case from Prisma @map annotations)
  first_name: z.string(),
  last_name: z.string(),
  role: z.string(),
  profile_picture: z.string(),
  profile_picture_sizes: z.unknown().nullable(),
  short_bio: z.string(),
  bio: z.string(),
  tags: z.string(),
  gender: z.string(),

  // AI configuration
  model: z.string().nullable(),
  modelConfig: ModelConfigSchema.nullable(),

  // Prompt — kept as z.unknown() to accommodate both legacy string fields
  // and the TipTap JSON document format without schema drift risk
  promptJson: z.unknown().nullable(),
  productionPromptVersionId: z.string().nullable(),
  knowledges: z.array(AgentKnowledgeSchema),

  // Legacy prompt fields (preserved until full TipTap migration)
  explanation: z.string().nullable(),
  objective: z.string().nullable(),
  tone: z.string().nullable(),
  voicePrompt: z.string().nullable(),
  behaviour: z.array(z.string()),

  // Channel format arrays
  format: z.array(z.string()),
  phoneFormat: z.array(z.string()),
  whatsappFormat: z.array(z.string()),
  gmailFormat: z.array(z.string()),
  outlookFormat: z.array(z.string()),
  slackFormat: z.array(z.string()),
  smsFormat: z.array(z.string()),
  teamsFormat: z.array(z.string()),
  messengerFormat: z.array(z.string()),
  instagramFormat: z.array(z.string()),
  telegramFormat: z.array(z.string()),
  twilioFormat: z.array(z.string()),
  apiFormat: z.array(z.string()),
  wordFormat: z.array(z.string()),

  // Runtime
  status: AgentStatusSchema,
  cacheable: z.boolean(),
  cacheTtl: z.enum(["5m", "1h"]).nullable(),
  playgroundFirstMessage: z.string().nullable(),
  codeInterpreterSkillsUrl: z.string().nullable(),

  // Additional fields
  details: z.string().nullable(),
  profile_picture_optimized: z.boolean(),
  profile_picture_migrated_at: z.coerce.date().nullable(),

  // Timestamps (coerce handles Date ↔ ISO string serialization)
  created_at: z.coerce.date(),
  updated_at: z.coerce.date().nullable(),

  // Relations
  agentToolConfigs: z.array(AgentToolConfigDetailSchema),
  deploymentTools: z.array(DeploymentToolSchema)
});

export type AgentDetail = z.infer<typeof AgentDetailSchema>;
